import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { 
  Bell, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Info, 
  Clock, 
  Search,
  Check,
  MoreVertical,
  ArrowLeft,
  Trash2
} from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../../components/ui/dropdown-menu";
import { cn } from "../../lib/utils";
import moment from 'moment';
import { toast } from "sonner";
import { usePostModal } from '../../contexts/PostModalContext';
import { useHistory } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

interface Notification {
  id: string;
  title: string;
  message: string;
  level: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  isRead: boolean;
  link?: string;
  createdAt: string;
}

export default function NotificationsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const history = useHistory();
  const token = localStorage.getItem('token');
  const { openPostById, openMediaById } = usePostModal();
  const [searchQuery, setSearchQuery] = React.useState('');

  const { data: notifications = [], isLoading } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: async () => {
      if (!token) return [];
      const response = await axios.get(`${API_BASE_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token,
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await axios.patch(`${API_BASE_URL}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await axios.patch(`${API_BASE_URL}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await axios.delete(`${API_BASE_URL}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      await axios.delete(`${API_BASE_URL}/notifications/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success("All notifications deleted");
    },
    onError: () => toast.error("Failed to delete notifications")
  });

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'SUCCESS': return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'ERROR': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'WARNING': return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      default: return <Info className="h-5 w-5 text-primary" />;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }

    if (notification.link) {
      const postMatch = notification.link.match(/\/posts\/([a-zA-Z0-9-]+)/);
      const mediaMatch = notification.link.match(/\/media\/([a-zA-Z0-9-]+)/);
      
      if (postMatch) {
        openPostById(postMatch[1]);
      } else if (mediaMatch) {
        openMediaById(mediaMatch[1]);
      }
    }
  };

  const filteredNotifications = notifications.filter(n => 
    n.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    n.message.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-muted-foreground hover:text-foreground"
          onClick={() => history.goBack()}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            Notifications
            {unreadCount > 0 && (
              <Badge className="bg-primary hover:bg-primary text-xs px-2 py-0.5 rounded-full">
                {unreadCount} new
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">Stay updated with your latest activity and scheduled posts</p>
        </div>
      </div>

      <Card className="bg-card border-border shadow-xl overflow-hidden">
        <CardHeader className="border-b border-border flex flex-row items-center justify-between gap-4 p-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search notifications..." 
              className="pl-10 bg-background border-border text-foreground"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
                onClick={() => markAllAsReadMutation.mutate()}
              >
                <Check className="h-4 w-4 mr-2" />
                Mark all as read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-red-600/10 border-red-500/30 text-red-400 hover:bg-red-600/20"
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete all notifications?")) {
                    deleteAllMutation.mutate();
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete all
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p>Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-4">
              <div className="h-20 w-20 rounded-full bg-card/50 flex items-center justify-center">
                <Bell className="h-10 w-10 opacity-20" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-bold text-foreground">No notifications found</h3>
                <p className="text-sm">We'll let you know when something important happens</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {filteredNotifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={cn(
                    "group p-6 cursor-pointer transition-all duration-200 hover:bg-card/50 flex gap-4 relative",
                    !notification.isRead && "bg-primary/[0.03]"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {!notification.isRead && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary shadow-[0_0_15px_rgba(37,99,235,0.5)]" />
                  )}
                  
                  <div className="mt-1 shrink-0 p-2 rounded-xl bg-card/50">
                    {getLevelIcon(notification.level)}
                  </div>
                  
                  <div className="flex-1 space-y-2 overflow-hidden pr-8">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={cn("text-lg font-bold transition-colors", !notification.isRead ? "text-foreground" : "text-muted-foreground")}>
                        {notification.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        {moment(notification.createdAt).fromNow()}
                      </div>
                    </div>
                    
                    <p className={cn("text-base leading-relaxed", !notification.isRead ? "text-foreground" : "text-muted-foreground")}>
                      {notification.message}
                    </p>
                    
                    {notification.link && (
                      <div className="pt-2">
                        <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary font-medium">
                          Click to view details
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-background border-border">
                        {!notification.isRead && (
                          <DropdownMenuItem 
                            className="text-primary focus:text-primary/80 focus:bg-primary/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsReadMutation.mutate(notification.id);
                            }}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Mark as read
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          className="text-red-400 focus:text-red-300 focus:bg-red-600/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotificationMutation.mutate(notification.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete notification
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Loader2(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

